// Floating, role-aware assistance launcher. Ties together the first-time
// welcome overlay, replayable guided tour, and the Help Center drawer.
//
// Mounted once in PortalShell so it appears across investor and GIS workspaces
// without touching routing, auth, or page-level code.

import React, { useEffect, useState } from 'react';
import { LifeBuoy, BookOpen, Compass, X, ChevronRight, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  hasSeenWelcome,
  resetTour,
  resetWelcome,
  type GuidanceRole,
} from '@/lib/onboarding';
import GuidedTour from './GuidedTour';
import HelpCenter from './HelpCenter';

/** Map the canonical auth role → coarse guidance role (no personal data). */
function toGuidanceRole(role: string | null | undefined): GuidanceRole {
  switch (role) {
    case 'admin':
    case 'giisdec_admin':
      return 'admin';
    case 'investor':
    case 'strategic_partner':
      return 'investor';
    case 'gis-analyst':
    case 'gis_analyst':
      return 'gis';
    default:
      return 'public';
  }
}

interface AssistanceLauncherProps {
  /** Optional jump handler so tour steps can navigate dashboard tabs. */
  onJump?: (tab: string) => void;
}

const AssistanceLauncher: React.FC<AssistanceLauncherProps> = ({ onJump }) => {
  const { user, role, loading } = useAuth();
  const guidanceRole = toGuidanceRole(role);

  const [menuOpen, setMenuOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStartInSteps, setTourStartInSteps] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // First-time welcome — only for authenticated users, only once per role.
  useEffect(() => {
    if (loading || !user) return;
    if (!hasSeenWelcome(guidanceRole)) {
      setTourStartInSteps(false);
      setTourOpen(true);
    }
  }, [loading, user, guidanceRole]);

  const replayTour = () => {
    resetTour(guidanceRole);
    resetWelcome(guidanceRole);
    setTourStartInSteps(true);
    setTourOpen(true);
    setMenuOpen(false);
  };

  const openHelp = () => {
    setHelpOpen(true);
    setMenuOpen(false);
  };

  return (
    <>
      <GuidedTour
        role={guidanceRole}
        open={tourOpen}
        startInSteps={tourStartInSteps}
        onClose={() => setTourOpen(false)}
        onJump={onJump}
      />
      <HelpCenter open={helpOpen} onClose={() => setHelpOpen(false)} />

      {/* Floating launcher — EDS enterprise styling */}
      <div className="fixed bottom-6 right-6 z-[90] flex flex-col items-end gap-3 font-eds-body">
        {menuOpen && (
          <div className="w-72 bg-eds-surface rounded-eds-lg shadow-eds-md border border-eds-border overflow-hidden">
            {/* Branded header */}
            <div className="relative overflow-hidden bg-eds-forest px-4 py-3.5">
              <div className="absolute -top-8 -right-8 w-24 h-24 rounded-eds-pill bg-eds-emerald/20 blur-2xl" aria-hidden="true" />
              <div className="relative flex items-center gap-2.5">
                <span className="w-9 h-9 rounded-eds-md bg-white/10 border border-white/15 flex items-center justify-center text-eds-mint">
                  <LifeBuoy className="w-[18px] h-[18px]" />
                </span>
                <div className="leading-tight">
                  <div className="text-sm font-semibold text-white">Assistance</div>
                  <div className="text-[11px] text-white/60">Guides &amp; support for the portal</div>
                </div>
              </div>
            </div>
            {/* Actions */}
            <div className="p-1.5">
              <button
                type="button"
                onClick={openHelp}
                className="group w-full flex items-center gap-3 px-3 py-2.5 rounded-eds-md text-left hover:bg-eds-surface-2 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring"
              >
                <span className="w-9 h-9 flex-shrink-0 rounded-eds-md bg-eds-success/10 border border-eds-success/20 flex items-center justify-center text-eds-emerald-2 group-hover:scale-105 transition">
                  <BookOpen className="w-[18px] h-[18px]" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-eds-fg">Help Center</span>
                  <span className="block text-sm font-medium text-eds-muted-fg">Search topics &amp; how-tos</span>
                </span>
                <ChevronRight className="w-4 h-4 ml-auto text-eds-muted-fg group-hover:translate-x-0.5 transition" />
              </button>
              <button
                type="button"
                onClick={replayTour}
                className="group w-full flex items-center gap-3 px-3 py-2.5 rounded-eds-md text-left hover:bg-eds-surface-2 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring"
              >
                <span className="w-9 h-9 flex-shrink-0 rounded-eds-md bg-eds-gold/12 border border-eds-gold/25 flex items-center justify-center text-eds-earth group-hover:scale-105 transition">
                  <Compass className="w-[18px] h-[18px]" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-eds-fg">Replay tour</span>
                  <span className="block text-sm font-medium text-eds-muted-fg">A quick guided walkthrough</span>
                </span>
                <ChevronRight className="w-4 h-4 ml-auto text-eds-muted-fg group-hover:translate-x-0.5 transition" />
              </button>
            </div>
            <div className="px-4 py-2.5 border-t border-eds-border bg-eds-surface-2/50 flex items-center gap-1.5 text-[11px] text-eds-muted-fg">
              <ShieldCheck className="w-3.5 h-3.5 text-eds-emerald-2" /> Hosted at the NITA National Data Centre
            </div>
          </div>
        )}
        <button
          type="button"
          aria-label={menuOpen ? 'Close assistance menu' : 'Open assistance menu'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
          className="group inline-flex items-center justify-center w-14 h-14 rounded-eds-pill bg-eds-forest text-white shadow-eds-md ring-1 ring-white/10 hover:bg-eds-forest-2 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-eds-mint focus-visible:ring-offset-2 focus-visible:ring-offset-eds-forest"
        >
          <span className="absolute w-14 h-14 rounded-eds-pill bg-eds-emerald/25 blur-md -z-10 opacity-0 group-hover:opacity-100 transition" aria-hidden="true" />
          {menuOpen ? <X className="w-5 h-5" /> : <LifeBuoy className="w-5 h-5 text-eds-mint" />}
        </button>
      </div>
    </>
  );
};

export default AssistanceLauncher;
